import type { AuditScoreType } from "@ots-frontend/ots-data";
import { createContext, type ReactNode, useContext } from "react";
import type { ActivityShiftActionId, ActivityShiftCost, ActivityShiftTarget } from "./driver-activity-actions";
import type { ActivityDensity } from "./driver-activity-layout";
import type { ActivityDurationMode, ActivityEntry, ActivityIdLabel, ActivityTransportEdit } from "./driver-activity-view-model";

/**
 * What every driver card and row of the grid shares: display settings, the selection, and what to do when a
 * row is clicked, edited, or right-clicked. The list provides them once instead of passing them down through
 * each card and row; a new row setting is added here, given a value by the list, and read where it is used.
 */
export type DriverActivityRowSettings = {
  timeZone?: string;
  /** The day being viewed; times on other days get a +1d / −1d badge. */
  day?: Date;
  /** "Now", for open shifts and running durations. */
  now?: number;
  showRevenue?: boolean;
  /**
   * Revenue and cost come from only some rate models, so a shift's end row sums its tickets instead of showing
   * the server's whole-shift figures.
   */
  rateModelsNarrowed?: boolean;
  /** Costing shifts from their end rows. */
  shiftCost?: ActivityShiftCost;
  /** Which of a ticket's times the Duration column shows. */
  durationMode?: ActivityDurationMode;
  density?: ActivityDensity;
  /** Shows audit rings on tickets and each driver's and shift's weakest audit. */
  showAudit?: boolean;
  auditScoreType?: AuditScoreType;
  /** What the ID column shows: the resource ID, or the paper ticket number when there is one. */
  idLabel?: ActivityIdLabel;
  /** Draws each driver's day as a strip in their header. */
  showTimeline?: boolean;
  /** Keys of the selected tickets. */
  selected?: ReadonlySet<string>;
  /** The row whose details are open in the inspector. */
  activeKey?: string;
  /** Row element ids are this prefix plus the entry key. */
  rowIdPrefix?: string;
  onToggleDriver?: (driverKey: string) => void;
  onToggleSelected?: (entry: ActivityEntry, options: { range: boolean }) => void;
  /** Selects or clears all of a driver's tickets. */
  onSelectEntries?: (entries: ActivityEntry[], selected: boolean) => void;
  onActivate?: (entry: ActivityEntry) => void;
  /** Opens a ticket's audit, from its audit ring. */
  onOpenAudit?: (entry: ActivityEntry) => void;
  /** Opens a ticket's attachments, from the attachment icons. */
  onOpenAttachments?: (entry: ActivityEntry) => void;
  /** Makes ticket numbers and quantities editable in place. */
  onEditTransport?: (entry: ActivityEntry, change: ActivityTransportEdit) => void | Promise<unknown>;
  /** Opens a ticket's tools menu where it was right-clicked. */
  onEntryContextMenu?: (entry: ActivityEntry, point: { x: number; y: number }) => void;
  /** The buttons each shift's start row offers, and what runs them. */
  shiftActionIds?: readonly ActivityShiftActionId[];
  onShiftAction?: (actionId: ActivityShiftActionId, target: ActivityShiftTarget) => void;
  /** Opens a driver's whole day on a map, from their header. */
  onOpenDriverMap?: (driverKey: string) => void;
};

/** Every setting, in a fixed order: what a memoized settings value depends on. */
export const DRIVER_ACTIVITY_ROW_SETTING_KEYS = [
  "timeZone", "day", "now", "showRevenue", "rateModelsNarrowed", "shiftCost", "durationMode", "density", "showAudit", "auditScoreType", "idLabel",
  "showTimeline", "selected", "activeKey", "rowIdPrefix", "onToggleDriver", "onToggleSelected", "onSelectEntries",
  "onActivate", "onOpenAudit", "onOpenAttachments", "onEditTransport", "onEntryContextMenu", "shiftActionIds", "onShiftAction", "onOpenDriverMap",
] as const satisfies readonly (keyof DriverActivityRowSettings)[];

const RowSettingsContext = createContext<DriverActivityRowSettings>({});

export const DriverActivityRowSettingsProvider = ({ value, children }: { value: DriverActivityRowSettings; children: ReactNode }) =>
  <RowSettingsContext.Provider value={value}>{children}</RowSettingsContext.Provider>;

/** The settings around a card or row, with any it was given directly taking their place. */
export const useDriverActivityRowSettings = (overrides: DriverActivityRowSettings = {}): DriverActivityRowSettings => {
  const context = useContext(RowSettingsContext);
  const merged: DriverActivityRowSettings = { ...context };
  for (const [key, value] of Object.entries(overrides) as [keyof DriverActivityRowSettings, unknown][]) {
    if (value !== undefined) (merged as Record<string, unknown>)[key] = value;
  }
  return merged;
};
